import { strToU8, zipSync } from 'fflate'
import { DB_VERSION } from '@/src/renderer/lib/db/models'
import {
  BACKUP_FORMAT, BACKUP_FORMAT_VERSION, countByLifeSpace,
  type BackupCryptoParams, type BackupData, type BackupDeviceInfo, type BackupManifest,
} from './format'

/**
 * Packt ein Backup: `manifest.json` (lesbar, ohne die Daten zu entpacken) und `data.json`.
 * Datumswerte werden ISO-Text; `unpackBackup` verwandelt sie je Tabelle zurück.
 */
export function packBackup(data: BackupData, device: BackupDeviceInfo, exportedAt: Date = new Date()): Uint8Array {
  const manifest: BackupManifest = {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    dbVersion: DB_VERSION,
    ...device,
    exportedAt: exportedAt.toISOString(),
    encrypted: false,
    accountId: data.accountId,
    counts: countByLifeSpace(data.tables),
  }
  return zipSync({
    'manifest.json': strToU8(JSON.stringify(manifest, null, 2)),
    'data.json': strToU8(JSON.stringify(data)),
  })
}

export type EncryptBackup = (plain: Uint8Array) => Promise<{ data: Uint8Array; params: BackupCryptoParams }>

/**
 * Packt ein **geschütztes** Backup (Entscheidung 17): `data.enc` statt `data.json`, verschlüsselt vom
 * Hauptprozess. Lesbar bleiben nur Datum, Gerät und Versionen — keine Anzahlen, kein Konto.
 */
export async function packProtectedBackup(
  data: BackupData,
  device: BackupDeviceInfo,
  encrypt: EncryptBackup,
  exportedAt: Date = new Date(),
): Promise<Uint8Array> {
  const sealed = await encrypt(strToU8(JSON.stringify(data)))
  const manifest: BackupManifest = {
    format: BACKUP_FORMAT,
    formatVersion: BACKUP_FORMAT_VERSION,
    dbVersion: DB_VERSION,
    ...device,
    exportedAt: exportedAt.toISOString(),
    encrypted: true,
    accountId: null,
    counts: {},
    crypto: sealed.params,
  }
  return zipSync({
    'manifest.json': strToU8(JSON.stringify(manifest, null, 2)),
    'data.enc': sealed.data,
  })
}

/** Was {@link packForDevice} vom Hauptprozess braucht — ein Ausschnitt der Backup-Brücke. */
export interface PackingBridge {
  deviceInfo(): Promise<BackupDeviceInfo>
  passwordStatus(): Promise<{ enabled: boolean }>
  encrypt: EncryptBackup
}

/** Packt, wie dieses Gerät es eingestellt hat: mit Passwort geschützt oder offen. Für Hand und Automatik. */
export async function packForDevice(data: BackupData, bridge: PackingBridge, exportedAt: Date = new Date()): Promise<Uint8Array> {
  const device = await bridge.deviceInfo()
  const { enabled } = await bridge.passwordStatus()
  return enabled ? packProtectedBackup(data, device, bridge.encrypt, exportedAt) : packBackup(data, device, exportedAt)
}

/** `HappyTask-Backup-JJJJ-MM-TT.happytask` — sprachunabhängig, nach dem lokalen Tag. */
export function backupFileName(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `HappyTask-Backup-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}.happytask`
}
